import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon';
import { IconButton } from '../components/ui';
import { topicById, type HelpSection, type HelpTopic } from './content';
import { useHelp } from './HelpProvider';

/**
 * The help panel: a slide-over on the right, opened at one topic.
 *
 * Chosen over a tooltip because most of what needs explaining here does not fit in a tooltip.
 * "Why is this session excluded" is a paragraph and a number, not a phrase, and a tooltip is
 * also invisible on touch and awkward from the keyboard.
 *
 * It does not trap focus or block the page. The panel explains what you are looking at, so
 * being able to keep looking at it — scroll the table, hover a row — while reading is the point.
 * Escape closes it and focus returns to whatever opened it.
 */
export function HelpPanel() {
  const { topic, close, open } = useHelp();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (topic) {
      opener.current = document.activeElement;
      panelRef.current?.focus();
      return;
    }
    // Focus goes back where it came from, or a keyboard user is dumped at the top of the page.
    if (opener.current instanceof HTMLElement && document.contains(opener.current)) {
      opener.current.focus();
    }
    opener.current = null;
  }, [topic]);

  useEffect(() => {
    if (!topic) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [topic, close]);

  if (!topic) return null;

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      role="complementary"
      aria-label={`Help: ${topic.title}`}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[440px] flex-col border-l border-border-strong bg-surface shadow-2xl outline-none"
    >
      <header className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="eyebrow">Help</p>
          <h2 className="m-0 text-[16px] font-semibold tracking-tight text-ink">{topic.title}</h2>
        </div>
        <IconButton label="Close help" onClick={close}>
          <Icon name="close" size={16} />
        </IconButton>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[12.5px] leading-[1.55]">
        <p className="mt-0 mb-3 text-ink-2">{topic.summary}</p>
        {topic.sections.map((section, i) => (
          <Section key={i} section={section} />
        ))}

        {topic.related && topic.related.length > 0 ? (
          <div className="mt-4 border-t border-border pt-3">
            <p className="eyebrow mb-1.5">See also</p>
            <ul className="flex flex-wrap gap-1.5">
              {topic.related.map((id) => {
                const other = topicById(id);
                if (!other) return null;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => open(id)}
                      className="rounded-panel border border-border px-1.5 py-0.5 text-[11.5px] text-ink-2 hover:border-accent hover:text-ink"
                    >
                      {other.title}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <footer className="border-t border-border px-4 py-2.5">
        <Link
          to={`/help#${topic.id}`}
          onClick={close}
          className="text-[12px] text-accent-ink underline-offset-2 hover:underline"
        >
          Open the full reference →
        </Link>
      </footer>
    </aside>
  );
}

/** Shared by the panel and the full reference, so the two cannot render a section differently. */
export function Section({ section }: { section: HelpSection }) {
  if (section.kind === 'prose') {
    return (
      <div className="mb-3">
        {section.heading ? <h3 className={HEADING}>{section.heading}</h3> : null}
        <p className="m-0 text-ink-2">{section.body}</p>
      </div>
    );
  }

  if (section.kind === 'terms') {
    return (
      <div className="mb-3">
        {section.heading ? <h3 className={HEADING}>{section.heading}</h3> : null}
        <dl className="m-0 flex flex-col gap-1.5">
          {section.items.map((item) => (
            <div key={item.term}>
              <dt className="font-semibold text-ink">{item.term}</dt>
              <dd className="m-0 text-ink-2">{item.body}</dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  if (section.kind === 'steps') {
    return (
      <div className="mb-3">
        {section.heading ? <h3 className={HEADING}>{section.heading}</h3> : null}
        <ol className="m-0 flex list-decimal flex-col gap-1.5 pl-4 text-ink-2">
          {section.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      </div>
    );
  }

  // `detail` — the internals. Set aside rather than removed: a collaborator wants them and
  // everyone else should be able to see at a glance that this paragraph is skippable.
  return (
    <details className="mb-3 rounded-panel border border-border bg-sunken px-2.5 py-1.5">
      <summary className="cursor-pointer text-[11.5px] font-semibold text-ink-3">
        {section.heading}
      </summary>
      <p className="mt-1.5 mb-0 text-[12px] text-ink-2">{section.body}</p>
    </details>
  );
}

const HEADING = 'mt-0 mb-1 text-[12.5px] font-semibold text-ink';

export type { HelpTopic };
